import { ReactElement, useRef } from 'react'
import { EuiPanel } from '@equipmentshare/ds2'
import { GridApiPremium } from '@mui/x-data-grid-premium'
import { FilterConfig, UseFetchFilterOptionsQuery } from '@equipmentshare/dcf-search-filter-experience'
import SearchFilterGrid from 'components/SearchFilterGrid'
import columns from './columns'
import useFulfillmentOrdersDataQuery, { FulfillmentOrderRow, FulfillmentOrdersSearchRequest } from './hooks/useFulfillmentOrdersDataQuery'
import {
	FulfillmentOrderStatus,
	useInventoryLocationsQuery,
	useDcAdminFulfillmentOrdersQuery,
	useDemandRequestsQuery,
} from '../../graphql'
import { FO_STATUS_LABEL } from './constants'
import { useDcAdminContext } from './contexts/DcAdminContext'
import parseOrderNumber from './parseOrderNumber'
import useFulfillmentOrderPickSelection from './pick-orders/useFulfillmentOrderPickSelection'
import CreatePickOrderAction from './pick-orders/components/CreatePickOrderAction'

const filters: FilterConfig<FulfillmentOrdersSearchRequest>[] = [
	{
		field: 'orderNumbers',
		label: 'FO #',
		type: 'list',
		displayOptionNarrowingInput: true,
		optionFilterMode: 'server',
		useIdValueForQuery: true,
		showByDefault: true,
	},
	{
		field: 'demandRequestIds',
		label: 'REQ #',
		type: 'list',
		displayOptionNarrowingInput: true,
		optionFilterMode: 'server',
		useIdValueForQuery: true,
		showByDefault: true,
	},
	{
		field: 'transferOrderIds',
		label: 'TO #',
		type: 'list',
		displayOptionNarrowingInput: true,
		optionFilterMode: 'server',
		useIdValueForQuery: true,
		showByDefault: true,
	},
	{
		field: 'statuses',
		label: 'Status',
		type: 'list',
		showByDefault: true,
	},
	{
		field: 'destinationInventoryIds',
		label: 'Destination',
		type: 'list',
		showByDefault: true,
	},
	{
		field: 'dateCreated',
		label: 'Created',
		type: 'date-range',
		showByDefault: true,
		defaultDateRange: null,
		startFilterField: 'dateCreatedAfter',
		endFilterField: 'dateCreatedBefore',
	},
]

const STATUS_OPTIONS = Object.values( FulfillmentOrderStatus ).map( ( status ) => ( {
	id: status,
	label: FO_STATUS_LABEL[ status ] ?? status,
	count: 0,
} ) )

export const useFetchFilterOptionsQuery: UseFetchFilterOptionsQuery<FulfillmentOrdersSearchRequest> = ( countBy, { enabled }, _apiFilterParams, filterQuery ) => {
	const { selectedDcId } = useDcAdminContext()

	const isDestinationFilter = countBy === 'destinationInventoryIds'
	const { data: locationsData, loading: locationsLoading } = useInventoryLocationsQuery( {
		skip: !isDestinationFilter || !enabled,
		variables: { paging: { first: 1000 } },
	} )

	// FO # — query fulfillmentOrders by exact order number (accepts an optional "FO-" prefix)
	const isFoNumberFilter = countBy === 'orderNumbers'
	const parsedFoNumber = isFoNumberFilter && filterQuery ? parseOrderNumber( filterQuery, 'FO' ) : undefined
	const { data: foLookupData, loading: foLookupLoading } = useDcAdminFulfillmentOrdersQuery( {
		skip: !isFoNumberFilter || parsedFoNumber === undefined || !enabled,
		variables: {
			fulfillingInventoryIds: selectedDcId ? [ selectedDcId ] : undefined,
			orderNumbers: parsedFoNumber !== undefined ? [ parsedFoNumber ] : undefined,
			paging: { first: 10 },
		},
		fetchPolicy: 'network-only',
	} )

	// REQ # — query demand requests by request number (accepts an optional "REQ-" prefix).
	// Not scoped to the selected DC: a routed stock request has no fulfilling DC
	// of its own (its owning location is the main DC), so a mini-DC would never
	// match. The fulfillment orders query this feeds is already scoped to the DC.
	const isDrFilter = countBy === 'demandRequestIds'
	const parsedDrNumber = isDrFilter && filterQuery ? parseOrderNumber( filterQuery, 'REQ' ) : undefined
	const { data: drLookupData, loading: drLookupLoading } = useDemandRequestsQuery( {
		skip: !isDrFilter || parsedDrNumber === undefined || !enabled,
		variables: {
			requestNumbers: parsedDrNumber !== undefined ? [ parsedDrNumber ] : undefined,
			paging: { first: 10 },
		},
		fetchPolicy: 'network-only',
	} )

	// Just a reminder that TOs use the "PK" as the transfer order number.
	const isToFilter = countBy === 'transferOrderIds'
	const parsedToNumber = isToFilter && filterQuery ? parseOrderNumber( filterQuery, 'TO' ) : undefined

	if ( countBy === 'statuses' ) {
		return { isFetching: false, data: { filterOptions: STATUS_OPTIONS } }
	}
	if ( isDestinationFilter ) {
		const filterOptions = locationsData?.inventoryLocations.edges.map( ( edge ) => ( {
			id: edge.node.id,
			label: edge.node.name,
			count: 0,
		} ) ) ?? []
		return { isFetching: locationsLoading, data: { filterOptions } }
	}
	if ( isFoNumberFilter ) {
		const filterOptions = foLookupData?.fulfillmentOrders.edges.map( ( edge ) => ( {
			id: String( edge.node.orderNumber ),
			label: `FO-${edge.node.orderNumber}`,
			count: 0,
		} ) ) ?? []
		return { isFetching: foLookupLoading && parsedFoNumber !== undefined, data: { filterOptions } }
	}
	if ( isDrFilter ) {
		const filterOptions = drLookupData?.demandRequests.edges.map( ( edge ) => ( {
			id: edge.node.id,
			label: `REQ-${edge.node.requestNumber}`,
			count: 0,
		} ) ) ?? []
		return { isFetching: drLookupLoading && parsedDrNumber !== undefined, data: { filterOptions } }
	}
	if ( isToFilter ) {
		const filterOptions = parsedToNumber !== undefined
			? [ { id: String( parsedToNumber ), label: `TO-${parsedToNumber}`, count: 0 } ]
			: []
		return { isFetching: false, data: { filterOptions } }
	}
	return { isFetching: false, data: { filterOptions: [] } }
}

export default function FulfillmentOrders(): ReactElement {
	const dataGridApiRef = useRef<GridApiPremium>( null )
	const picking = useFulfillmentOrderPickSelection( dataGridApiRef )

	return (
		<EuiPanel paddingSize="none" hasShadow={false} style={{ height: '100%', overflow: 'auto' }}>
			<SearchFilterGrid<FulfillmentOrderRow, FulfillmentOrdersSearchRequest>
				dataGridName="dc-admin-fulfillment-orders-table"
				filters={filters}
				filterStateMode="url"
				columns={columns}
				pageSizeOptions={[ 25, 50, 100 ]}
				useFetchQuery={useFulfillmentOrdersDataQuery}
				useFetchFilterOptionsQuery={useFetchFilterOptionsQuery}
				searchPlaceholder=""
				hideSearch
				getRowId={( row ) => row.id}
				dataGridApiRef={dataGridApiRef}
				// Checking orders here is how a pick order gets started, so the
				// column only appears for someone who could actually start one.
				checkboxSelection={picking.enabled}
				isRowSelectable={picking.isRowSelectable}
				rowSelectionModel={picking.selectionModel}
				onRowSelectionModelChange={picking.onSelectionModelChange}
				// Paging replaces the whole row model, so without this a run cannot
				// span pages — the boxes ticked on page one quietly come undone.
				keepNonExistentRowsSelected
				actionSlot={picking.enabled ? (
					<CreatePickOrderAction
						fulfillingInventoryId={picking.fulfillingInventoryId}
						orders={picking.selected}
					/>
				) : undefined}
			/>
		</EuiPanel>
	)
}
